import { useState, useCallback, useEffect, useMemo } from "react";
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, ReferenceDot } from "recharts";

import { API } from "../config";
import IconaInfoModal from "../components/IconaInfoModal";

// ===== STILI BASE (coerenti con CreazioneSistemi.js / GestioneStrategie.js) =====
const btnBase = {
  fontFamily:"'DM Sans',sans-serif",fontSize:12,fontWeight:600,
  border:"none",borderRadius:8,padding:"8px 16px",cursor:"pointer",
  display:"inline-flex",alignItems:"center",gap:6,
  transition:"opacity 0.15s",
};
const btnPrimary = {...btnBase,background:"#2563EB",color:"#fff"};
const btnSave = {...btnBase,background:"#16A34A",color:"#fff"};
const btnGhost = {...btnBase,background:"transparent",color:"#6B6B66",border:"1px solid #E8E8E4"};

// ===== POPOVER ESPLICATIVI KPI ESPOSIZIONE (v185) =====
const INFO_ESP_MEDIA_SISTEMA = (
  <IconaInfoModal titolo="Esposizione Media">
    <p style={{margin:"0 0 12px 0"}}>
      L'<b>Esposizione Media</b> è il rischio teorico medio per evento del sistema. Misura "quanto rischi in media, ogni volta che il sistema entra a mercato".
    </p>
    <p style={{margin:"0 0 8px 0"}}><b>Calcolo del rischio teorico per singolo evento:</b></p>
    <ul style={{margin:"0 0 12px 0",paddingLeft:20,listStyleType:"disc"}}>
      <li style={{marginBottom:4}}>Ingresso in Punta: rischio = <b>1</b> (capitale impegnato)</li>
      <li style={{marginBottom:4}}>Ingresso in Banca: rischio = <b>quota − 1</b> (capitale potenzialmente perso se l'evento si verifica)</li>
    </ul>
    <p style={{margin:"0 0 12px 0"}}>
      <b>Esposizione Media</b>: somma del rischio teorico di tutti gli eventi ÷ numero di eventi.
    </p>
    <p style={{margin:"0 0 8px 0"}}><b>Esempio:</b></p>
    <ul style={{margin:"0 0 0 0",paddingLeft:20,listStyleType:"disc"}}>
      <li style={{marginBottom:4}}>3 eventi in Punta → rischio totale = 3.00</li>
      <li style={{marginBottom:4}}>2 eventi in Banca quota 3.50 → rischio totale = 2 × 2.50 = 5.00</li>
      <li style={{marginBottom:4}}>Cumulato su 5 eventi = 8.00</li>
      <li style={{marginBottom:4}}><b>Esposizione Media = 8.00 / 5 = 1.60</b></li>
    </ul>
  </IconaInfoModal>
);

const INFO_ESP_GIORN_SISTEMA = (
  <IconaInfoModal titolo="Esposizione Max Giornaliera">
    <p style={{margin:"0 0 12px 0"}}>
      L'<b>Esposizione Max Giornaliera</b> è il massimo rischio teorico accumulato in una singola giornata nella storia del sistema. Indica il picco di esposizione sostenuto in 24 ore.
    </p>
    <p style={{margin:"0 0 12px 0"}}>
      <b>Calcolo</b>: per ogni giornata con almeno un evento, somma il rischio teorico di tutti gli eventi della giornata. L'Esposizione Max Giornaliera è il massimo storico tra queste somme.
    </p>
    <p style={{margin:"0 0 8px 0"}}><b>Esempio:</b></p>
    <ul style={{margin:"0 0 12px 0",paddingLeft:20,listStyleType:"disc"}}>
      <li style={{marginBottom:4}}>Sabato: 2 eventi (Punta 1.00 + Banca quota 3.00 → 2.00) = 3.00</li>
      <li style={{marginBottom:4}}>Domenica: 1 evento (Punta) = 1.00</li>
      <li style={{marginBottom:4}}>Lunedì: 3 eventi (Banca 2.50 → 1.50, Banca 4.00 → 3.00, Punta 1.00) = 5.50</li>
    </ul>
    <p style={{margin:0}}>
      Su questo storico, <b>Esposizione Max Giornaliera = 5.50</b> (giornata più carica).
    </p>
  </IconaInfoModal>
);

const INFO_ESP_SETT_SISTEMA = (
  <IconaInfoModal titolo="Esposizione Max Settimanale">
    <p style={{margin:"0 0 12px 0"}}>
      L'<b>Esposizione Max Settimanale</b> è il massimo rischio teorico accumulato in un singolo ciclo operativo nella storia del sistema. Indica il picco di esposizione sostenuto sull'arco di un ciclo intero.
    </p>
    <p style={{margin:"0 0 8px 0"}}><b>Cicli operativi della piattaforma:</b></p>
    <ul style={{margin:"0 0 12px 0",paddingLeft:20,listStyleType:"disc"}}>
      <li style={{marginBottom:4}}><b>Weekend</b>: da venerdì a lunedì compresi</li>
      <li style={{marginBottom:4}}><b>Midweek</b>: da martedì a giovedì compresi</li>
    </ul>
    <p style={{margin:"0 0 12px 0"}}>
      <b>Calcolo</b>: per ogni ciclo storico con almeno un evento, somma il rischio teorico di tutti gli eventi del ciclo. L'Esposizione Max Settimanale è il massimo storico tra queste somme.
    </p>
    <p style={{margin:"0 0 8px 0"}}><b>Esempio</b> (ciclo Weekend 13-16 marzo):</p>
    <ul style={{margin:"0 0 12px 0",paddingLeft:20,listStyleType:"disc"}}>
      <li style={{marginBottom:4}}>Venerdì: 2 eventi → 3.00</li>
      <li style={{marginBottom:4}}>Sabato: 1 evento → 2.00</li>
      <li style={{marginBottom:4}}>Domenica: 1 evento → 1.00</li>
      <li style={{marginBottom:4}}>Lunedì: 2 eventi → 4.00</li>
      <li style={{marginBottom:4}}>Totale ciclo = 10.00</li>
    </ul>
    <p style={{margin:0}}>
      Se questo è il massimo storico, <b>Esposizione Max Settimanale = 10.00</b>.
    </p>
  </IconaInfoModal>
);

// ===== HELPER GRAFICI EQUITY / DRAWDOWN =====

// Formatta YYYY-MM-DD in dd/mm/yyyy italiano
function _fmtDataIT(s) {
  if (!s || typeof s !== "string") return s || "";
  const p = s.split("-");
  if (p.length < 3) return s;
  return `${p[2]}/${p[1]}/${p[0]}`;
}

// Formatta la coppia (quota_media_punta, quota_media_banca) in stringa coerente
// con i KPI globali: se entrambi presenti -> "qp / qb", altrimenti il singolo, altrimenti "—".
function _fmtQuotaMedia(qp, qb) {
  const hasP = qp !== null && qp !== undefined;
  const hasB = qb !== null && qb !== undefined;
  if (hasP && hasB) return `${Number(qp).toFixed(2)} / ${Number(qb).toFixed(2)}`;
  if (hasP) return Number(qp).toFixed(2);
  if (hasB) return Number(qb).toFixed(2);
  return "—";
}

// Trova gli indici (rispetto all'array equity_line) in cui cambia la stagione.
// Restituisce array di { idx, stagione } per ogni cambio (escluso il primo).
function _calcolaCambiStagione(equity) {
  if (!Array.isArray(equity) || equity.length === 0) return [];
  const cambi = [];
  let prev = equity[0]?.stagione;
  for (let i = 1; i < equity.length; i++) {
    const s = equity[i]?.stagione;
    if (s && s !== prev) {
      cambi.push({ idx: i, stagione: s, data: equity[i].data });
      prev = s;
    }
  }
  return cambi;
}

// Tooltip riusabile per Equity (mostra: data, evento N di tot, punti, stagione)
function _makeTooltipEquity(totale) {
  return function TooltipEquity({ active, payload }) {
    if (!active || !payload || !payload.length) return null;
    const p = payload[0].payload;
    return (
      <div style={{background:"#fff",border:"1px solid #E8E8E4",borderRadius:8,
        boxShadow:"0 4px 12px rgba(0,0,0,0.08)",padding:"8px 10px",
        fontFamily:"'DM Sans',sans-serif",fontSize:11}}>
        <div style={{color:"#1E293B",fontWeight:600,marginBottom:2}}>
          {_fmtDataIT(p.data)} {p.stagione ? `· stag. ${p.stagione}` : ""}
        </div>
        <div style={{color:"#64748B",marginBottom:4}}>
          Evento {p.n} di {totale}
        </div>
        <div style={{color: p.punti_prog >= 0 ? "#15803D" : "#DC2626",fontWeight:700}}>
          {p.punti_prog >= 0 ? "+" : ""}{Number(p.punti_prog).toFixed(2)} pt
        </div>
      </div>
    );
  };
}

// Tooltip riusabile per Drawdown
function _makeTooltipDrawdown(totale) {
  return function TooltipDrawdown({ active, payload }) {
    if (!active || !payload || !payload.length) return null;
    const p = payload[0].payload;
    const dd = Number(p.drawdown) || 0;
    return (
      <div style={{background:"#fff",border:"1px solid #E8E8E4",borderRadius:8,
        boxShadow:"0 4px 12px rgba(0,0,0,0.08)",padding:"8px 10px",
        fontFamily:"'DM Sans',sans-serif",fontSize:11}}>
        <div style={{color:"#1E293B",fontWeight:600,marginBottom:2}}>
          {_fmtDataIT(p.data)} {p.stagione ? `· stag. ${p.stagione}` : ""}
        </div>
        <div style={{color:"#64748B",marginBottom:4}}>
          Evento {p.n} di {totale}
        </div>
        <div style={{color: dd > 0 ? "#DC2626" : "#94A3B8",fontWeight:700}}>
          {dd > 0 ? `-${dd.toFixed(2)}` : "0"} pt
        </div>
      </div>
    );
  };
}

export default function AnalisiSistemi({ sistemaIdIniziale, onTornaIndietro } = {}) {
  // --- Lista sistemi ---
  const [sistemi, setSistemi] = useState([]);
  const [loadingListe, setLoadingListe] = useState(true);

  // --- Sistema selezionato ---
  const [selId, setSelId] = useState("");

  // --- Vista corrente: "lista" | "analisi" ---
  const [vista, setVista] = useState("lista");

  // --- Analisi ---
  const [analisi, setAnalisi] = useState(null);
  const [analisiLoading, setAnalisiLoading] = useState(false);
  const [analisiErrore, setAnalisiErrore] = useState(null);

  // --- Estrazione eventi ---
  const [estrazione, setEstrazione] = useState(null);
  const [estrazioneLoading, setEstrazioneLoading] = useState(false);
  const [estrazioneErrore, setEstrazioneErrore] = useState(null);
  const [colonneExport, setColonneExport] = useState(null);
  // --- Drawer visualizza eventi ---
  const [drawerAperto, setDrawerAperto] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerPartite, setDrawerPartite] = useState([]);
  const COLONNE_DEFAULT = ["stagione","lega","data","time_it","casa","trasferta","mercato","quota","punti"];
  const COLONNE_LABELS = {stagione:"Stagione",lega:"Lega",data:"Data",time_it:"Ora",
    casa:"Casa",trasferta:"Trasferta",mercato:"Mercato",quota:"Quota",punti:"Punti"};

  // ===== CARICAMENTO INIZIALE =====
  const caricaSistemi = useCallback(async () => {
    try {
      const r = await fetch(`${API}/sistemi`);
      if (!r.ok) {
        const txt = await r.text().catch(() => "");
        throw new Error(`HTTP ${r.status}: ${txt}`);
      }
      const data = await r.json();
      setSistemi(data);
    } catch (e) { setAnalisiErrore(`Caricamento sistemi: ${e.message}`); }
  }, []);

  useEffect(() => {
    caricaSistemi().finally(() => setLoadingListe(false));
  }, [caricaSistemi]);

  // ===== CAMBIO SELEZIONE =====
  const cambiaSistema = useCallback((id) => {
    setSelId(id);
    setAnalisi(null);
    setAnalisiErrore(null);
    setEstrazione(null);
    setEstrazioneErrore(null);
    setColonneExport(null);
    setVista("lista");
  }, []);

  // ===== APRI ANALISI DA TABELLA =====
  const apriAnalisi = useCallback(async (id) => {
    setSelId(id);
    setAnalisi(null);
    setAnalisiErrore(null);
    setEstrazione(null);
    setEstrazioneErrore(null);
    setColonneExport(null);
    setVista("analisi");
    setAnalisiLoading(true);
    try {
      const r = await fetch(`${API}/sistemi/${id}/analisi`, {
        method: "POST",
        headers: {"Content-Type":"application/json"},
        body: "{}",
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || "Errore analisi");
      }
      const data = await r.json();
      if (data.per_stagione && !data.stagioni) {
        data.stagioni = Object.entries(data.per_stagione).map(([stagione, v]) => ({
          stagione, ...v,
        }));
      }
      setAnalisi(data);
    } catch (e) { setAnalisiErrore(e.message); }
    finally { setAnalisiLoading(false); }
  }, []);

  // ===== APERTURA DIRETTA SE INVOCATO COME EMBEDDED =====
  // Se il componente riceve sistemaIdIniziale (uso da Portfolio Sistemi),
  // salta la vista lista e apre subito la scheda Analisi del sistema specificato.
  useEffect(() => {
    if (sistemaIdIniziale) {
      apriAnalisi(sistemaIdIniziale);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sistemaIdIniziale]);

  // ===== ANALISI =====
  const toggleAttivoSistema = useCallback(async () => {
    if (!selId) return;
    const sistema = sistemi.find(s => s.id === selId);
    if (!sistema) return;
    const nuovoValore = !sistema.attivo;
    try {
      const res = await fetch(`${API}/sistemi/${selId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...sistema, attivo: nuovoValore }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSistemi(prev => prev.map(s => s.id === selId ? { ...s, attivo: nuovoValore } : s));
    } catch (e) {
      alert("Errore aggiornamento: " + e.message);
    }
  }, [selId, sistemi]);

  const eseguiAnalisi = useCallback(async () => {
    if (!selId) return;
    setAnalisiLoading(true); setAnalisiErrore(null);
    setEstrazione(null); setEstrazioneErrore(null); setColonneExport(null);
    try {
      const r = await fetch(`${API}/sistemi/${selId}/analisi`, {
        method: "POST",
        headers: {"Content-Type":"application/json"},
        body: "{}",
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || "Errore analisi");
      }
      const data = await r.json();
      if (data.per_stagione && !data.stagioni) {
        data.stagioni = Object.entries(data.per_stagione).map(([stagione, v]) => ({
          stagione, ...v,
        }));
      }
      setAnalisi(data);
    } catch (e) { setAnalisiErrore(e.message); }
    finally { setAnalisiLoading(false); }
  }, [selId]);

  // ===== ESTRAI EVENTI =====
  const eseguiEstrazione = useCallback(async () => {
    if (!selId) return;
    setEstrazioneLoading(true); setEstrazioneErrore(null);
    try {
      const r = await fetch(`${API}/sistemi/${selId}/estrai`, {
        method: "POST",
        headers: {"Content-Type":"application/json"},
        body: "{}",
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || "Errore estrazione");
      }
      const data = await r.json();
      setEstrazione(data);
    } catch (e) { setEstrazioneErrore(e.message); }
    finally { setEstrazioneLoading(false); }
  }, [selId]);

  // ===== EXPORT EXCEL =====
  const esportaExcel = useCallback(async () => {
    if (!selId) return;
    try {
      const r = await fetch(`${API}/sistemi/${selId}/estrai/excel`, { method: "POST" });
      if (!r.ok) { const err = await r.json().catch(() => ({})); alert(err.detail || "Errore export"); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sistema_${selId}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { alert("Errore: " + e.message); }
  }, [selId]);

  const estraiEventiDaLista = useCallback(async (id) => {
    try {
      const r = await fetch(`${API}/sistemi/${id}/estrai/excel`, { method: "POST" });
      if (!r.ok) { const err = await r.json().catch(() => ({})); alert(err.detail || "Errore export"); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sistema_${id}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { alert("Errore: " + e.message); }
  }, []);

  // ===== DRAWER VISUALIZZA EVENTI =====
  const apriDrawer = useCallback(async () => {
    if (!selId) return;
    setDrawerAperto(true);
    setDrawerLoading(true);
    setDrawerPartite([]);
    try {
      const r = await fetch(`${API}/sistemi/${selId}/estrai`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: "{}",
      });
      if (!r.ok) { const err = await r.json().catch(() => ({})); alert(err.detail || "Errore caricamento eventi"); setDrawerAperto(false); return; }
      const data = await r.json();
      setDrawerPartite(data.partite || []);
    } catch (e) { alert("Errore: " + e.message); setDrawerAperto(false); }
    finally { setDrawerLoading(false); }
  }, [selId]);

  // Nome del sistema selezionato
  const nomeSistema = sistemi.find(s => s.id === selId)?.nome || "";

  // Memo per i grafici Equity / Drawdown
  const equityLine = analisi?.equity_line || [];
  const cambiStagione = useMemo(() => _calcolaCambiStagione(equityLine), [equityLine]);
  const totaleEventi = equityLine.length;
  const TooltipEquity = useMemo(() => _makeTooltipEquity(totaleEventi), [totaleEventi]);
  const TooltipDrawdown = useMemo(() => _makeTooltipDrawdown(totaleEventi), [totaleEventi]);
  // Punto di Max P/L sull'equity (primo indice in cui punti_prog raggiunge il massimo)
  const puntoMaxPL = useMemo(() => {
    if (!equityLine.length) return null;
    let maxV = -Infinity, idxMax = -1;
    for (let i = 0; i < equityLine.length; i++) {
      const v = equityLine[i].punti_prog;
      if (v != null && v > maxV) { maxV = v; idxMax = i; }
    }
    return idxMax >= 0 ? equityLine[idxMax] : null;
  }, [equityLine]);
  // Ticks date dei cambi stagione + mappa data->etichetta stagione per renderle sull'asse X
  const ticksStagioni = useMemo(() => cambiStagione.map(cs => cs.data), [cambiStagione]);
  const mappaDataStagione = useMemo(() => {
    const m = {};
    cambiStagione.forEach(cs => { m[cs.data] = cs.stagione; });
    return m;
  }, [cambiStagione]);

  // ==================== RENDER ====================
  return (
    <div style={{minHeight:"100vh", background:"#FAFBFC", fontFamily:"'DM Sans',-apple-system,sans-serif"}}>

      {/* ===== HEADER PAGINA ===== */}
      <div style={{padding:"24px 32px 0 32px"}}>
        <h1 style={{fontSize:22,fontWeight:700,color:"#1E293B",marginBottom:4,letterSpacing:"-0.02em"}}>
          📈 Analisi Sistema
        </h1>
        <p style={{fontSize:13,color:"#64748B",marginBottom:24}}>
          Visualizza le statistiche e le performance del sistema.
        </p>
      </div>

      {loadingListe ? (
        <div style={{padding:40,textAlign:"center",color:"#94A3B8"}}>
          ⏳ Caricamento dati...
        </div>
      ) : vista === "lista" ? (

        /* ==================== VISTA LISTA ==================== */
        <div style={{padding:"0 32px 24px 32px"}}>
          {sistemi.length === 0 ? (
            <div style={{textAlign:"center",padding:60,color:"#94A3B8"}}>
              <div style={{fontSize:32,marginBottom:12}}>📊</div>
              <div style={{fontSize:13}}>Nessun sistema salvato.</div>
            </div>
          ) : (
            <div style={{background:"#fff",border:"1px solid #E8E8E4",borderRadius:10,overflow:"hidden"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                <thead>
                  <tr style={{background:"#F8F8F5",borderBottom:"1px solid #E8E8E4"}}>
                    {["Nome","Mercato","Stato","Commenti","Azioni"].map(h => (
                      <th key={h} style={{
                        padding:"10px 16px",textAlign: h === "Azioni" ? "center" : "left",
                        fontSize:10,fontWeight:700,color:"#94A3B8",
                        textTransform:"uppercase",letterSpacing:"0.06em",
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sistemi.map(s => (
                    <tr key={s.id}
                      style={{borderBottom:"1px solid #F4F4F0",transition:"background 0.1s"}}
                      onMouseEnter={e => e.currentTarget.style.background="#F8FAFF"}
                      onMouseLeave={e => e.currentTarget.style.background=""}>
                      <td style={{padding:"10px 16px",fontWeight:600,color:"#1E293B"}}>{s.nome}</td>
                      <td style={{padding:"10px 16px",fontSize:12,color:"#475569"}}>{s.mercato || "—"}</td>
                      <td style={{padding:"10px 16px"}}>
                        <span style={{
                          fontSize:11,fontWeight:700,padding:"2px 10px",borderRadius:12,
                          background: s.attivo ? "#DCFCE7" : "#F1F5F9",
                          color: s.attivo ? "#15803D" : "#94A3B8",
                        }}>
                          {s.attivo ? "✅ Attivo" : "⬜ Non attivo"}
                        </span>
                      </td>
                      <td style={{padding:"10px 16px",fontSize:12,color:"#475569"}}>{s.commenti || "—"}</td>
                      <td style={{padding:"10px 16px",textAlign:"center"}}>
                        <div style={{display:"flex",gap:6,justifyContent:"center"}}>
                          <button onClick={() => apriAnalisi(s.id)}
                            style={{...btnPrimary,fontSize:11,padding:"5px 14px"}}>
                            📊 Analizza
                          </button>
                          <button onClick={() => estraiEventiDaLista(s.id)}
                            style={{...btnSave,fontSize:11,padding:"5px 14px"}}>
                            📥 Estrai eventi
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      ) : (

        /* ==================== VISTA ANALISI ==================== */
        <div style={{padding:"0 32px 24px 32px"}}>

          {/* Torna all'elenco + Estrai eventi */}
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
            <button onClick={() => {
                if (onTornaIndietro) {
                  onTornaIndietro();
                } else {
                  setVista("lista"); setAnalisi(null); setAnalisiErrore(null);
                }
              }}
              style={{...btnGhost,fontSize:12}}>
              ← {onTornaIndietro ? "Portfolio Sistemi" : "Torna all'elenco"}
            </button>
            <div style={{display:"flex",gap:8}}>
              <button onClick={apriDrawer}
                style={{...btnGhost,fontSize:12,padding:"6px 14px"}}>
                👁 Visualizza eventi
              </button>
              <button onClick={esportaExcel}
                style={{...btnSave,fontSize:12,padding:"6px 14px"}}>
                📥 Estrai eventi
              </button>
            </div>
          </div>

          {/* Titolo sistema */}
          <div style={{marginBottom:16,display:"flex",alignItems:"center",gap:12}}>
            <span style={{fontSize:16,fontWeight:700,color:"#1E293B"}}>{nomeSistema}</span>
            {(() => {
              const sis = sistemi.find(s => s.id === selId);
              const attivo = sis?.attivo === true;
              return (
                <span style={{
                  fontSize:11,fontWeight:700,padding:"2px 10px",borderRadius:12,
                  background: attivo ? "#DCFCE7" : "#F1F5F9",
                  color: attivo ? "#15803D" : "#94A3B8",
                }}>
                  {attivo ? "✅ Attivo" : "⬜ Non attivo"}
                </span>
              );
            })()}
          </div>

          {/* Errore analisi */}
          {analisiErrore && (
            <div style={{marginBottom:12,padding:"8px 14px",background:"#FEF2F2",
              border:"1px solid #FECACA",borderRadius:8,fontSize:12,color:"#DC2626",
              display:"flex",alignItems:"center",gap:8}}>
              <span>⚠</span> {analisiErrore}
              <button onClick={() => setAnalisiErrore(null)}
                style={{marginLeft:"auto",background:"none",border:"none",
                  cursor:"pointer",color:"#DC2626",fontWeight:700}}>✕</button>
            </div>
          )}

          {/* Loading analisi */}
          {analisiLoading && (
            <div style={{textAlign:"center",padding:40,color:"#94A3B8"}}>
              ⏳ Calcolo analisi per "{nomeSistema}" in corso...
            </div>
          )}

          {/* Risultati analisi */}
          {analisi && !analisiLoading && (
            <>
              {/* ===== 12 KPI ===== */}
              <div style={{display:"flex",flexWrap:"wrap",background:"#fff",
                border:"1px solid #E8E8E4",borderRadius:10,overflow:"hidden",marginBottom:16}}>
                {[
                  {label:"Eventi", val: analisi.eventi?.toLocaleString("it-IT"), color:"#1A1A18"},
                  {label:"Punti Totali", val: (analisi.punti >= 0 ? "+" : "") + analisi.punti?.toFixed(2), color: analisi.punti >= 0 ? "#15803D" : "#DC2626"},
                  {label:"PPE", val: (analisi.ppe >= 0 ? "+" : "") + analisi.ppe?.toFixed(2), color: analisi.ppe >= 0 ? "#15803D" : "#DC2626"},
                  {label:"Max P/L", val: (() => {
                    const eq = analisi.equity_line || [];
                    if (!eq.length) return null;
                    const mx = Math.max(...eq.map(p => p.punti_prog || 0), 0);
                    return mx.toFixed(2);
                  })(), color:"#15803D"},
                  {label:"Quota Media", val: (() => {
                    const qp = analisi.quota_media_punta;
                    const qb = analisi.quota_media_banca;
                    const hasP = qp !== null && qp !== undefined;
                    const hasB = qb !== null && qb !== undefined;
                    if (hasP && hasB) return `${Number(qp).toFixed(2)} / ${Number(qb).toFixed(2)}`;
                    if (hasP) return Number(qp).toFixed(2);
                    if (hasB) return Number(qb).toFixed(2);
                    return analisi.quota_media?.toFixed(2);
                  })(), color:"#1A1A18"},
                  {label:"Win Rate", val: analisi.pct_vincenti?.toFixed(1) + "%", color:"#1A1A18"},
                  {label:"Max Win Run", val: analisi.max_win_run, color:"#15803D"},
                  {label:"Max Lose Run", val: analisi.max_lose_run, color:"#DC2626"},
                  {label:"Max Drawdown", val: analisi.max_drawdown?.toFixed(2), color:"#DC2626"},
                  {label:"Esp. Media", val: analisi.esp_media?.toFixed(2), color:"#7C3AED", info: INFO_ESP_MEDIA_SISTEMA},
                  {label:"Esp. Max Giorn.", val: analisi.espo_max_giornaliera?.toFixed(2), color:"#7C3AED", info: INFO_ESP_GIORN_SISTEMA},
                  {label:"Esp. Max Sett.", val: analisi.espo_max_settimanale?.toFixed(2), color:"#7C3AED", info: INFO_ESP_SETT_SISTEMA},
                  {label:"Size Ingresso", val: analisi.size_ingresso != null ? (analisi.size_ingresso * 100).toFixed(2) + "%" : "—", color:"#0369A1"},
                  {label:"Esp. Max Sett. Capitale", val: analisi.esp_max_set_capitale != null ? analisi.esp_max_set_capitale?.toFixed(1) + "%" : "—", color:"#0369A1"},
                ].map(({label, val, color, info}) => (
                  <div key={label} style={{flex:"1 1 100px",minWidth:100,
                    padding:"8px 12px",borderRight:"1px solid #F0F0EC",
                    borderBottom:"1px solid #F0F0EC",textAlign:"center"}}>
                    <div style={{fontSize:9,fontWeight:700,color:"#94A3B8",
                      textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:3,
                      display:"inline-flex",alignItems:"center",justifyContent:"center"}}>
                      {label}{info}
                    </div>
                    <div style={{fontSize:15,fontWeight:700,color,
                      fontFamily:"'DM Sans',sans-serif"}}>
                      {val ?? "—"}
                    </div>
                  </div>
                ))}
              </div>

              {/* ===== VARIAZIONE NEL TEMPO ===== */}
              {analisi.variazione_tempo && analisi.variazione_tempo.length > 0 && (
                <div style={{background:"#fff",border:"1px solid #E8E8E4",
                  borderRadius:10,overflow:"hidden",marginBottom:16}}>
                  <div style={{padding:"6px 14px",background:"#1B2846"}}>
                    <span style={{fontSize:10,fontWeight:700,color:"#ffffff",
                      textTransform:"uppercase",letterSpacing:"0.06em"}}>
                      Variazione nel tempo
                    </span>
                  </div>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
                    <thead>
                      <tr>
                        {["Statistica",...analisi.variazione_tempo.map(v => v.periodo)].map(h => (
                          <th key={h} style={{background:"#E8EDF5",padding:"5px 10px",
                            textAlign: h === "Statistica" ? "left" : "right",
                            fontSize:9,fontWeight:700,color:"#1B2846",
                            fontFamily:"'DM Sans',sans-serif",
                            textTransform:"uppercase",letterSpacing:"0.04em",
                            borderBottom:"1px solid #E8E8E4"}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {["Punti","Eventi","PPE"].map(stat => (
                        <tr key={stat}>
                          <td style={{padding:"5px 10px",fontWeight:600,
                            borderBottom:"1px solid #F4F4F0"}}>{stat}</td>
                          {analisi.variazione_tempo.map(v => {
                            const val = stat === "Punti" ? v.punti : stat === "Eventi" ? v.eventi : v.ppe;
                            const isNum = stat !== "Eventi";
                            return (
                              <td key={v.periodo} style={{padding:"5px 10px",textAlign:"right",
                                fontFamily:"'DM Sans',sans-serif",
                                color: isNum ? (val >= 0 ? "#15803D" : "#DC2626") : "#1A1A18",
                                fontWeight: stat === "Punti" ? 600 : 400,
                                borderBottom:"1px solid #F4F4F0"}}>
                                {stat === "Eventi" ? val : (val >= 0 ? "+" : "") + val?.toFixed(2)}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* ===== TABELLA BREAKDOWN STAGIONI ===== */}
              {analisi.stagioni && analisi.stagioni.length > 0 && (
                <div style={{background:"#fff",border:"1px solid #E8E8E4",
                  borderRadius:10,overflow:"hidden",marginBottom:16}}>
                  <div style={{padding:"6px 14px",background:"#1B2846"}}>
                    <span style={{fontSize:10,fontWeight:700,color:"#ffffff",
                      textTransform:"uppercase",letterSpacing:"0.06em"}}>
                      Breakdown per stagione
                    </span>
                  </div>
                  <div style={{maxHeight:220,overflowY:"auto"}}>
                    <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
                      <thead>
                        <tr>
                          {["Stagione","Eventi","% Ev.","Punti","PPE","Win Rate","Max DD","Quota Media","Esp. Media","Esp. Max Giorn.","Esp. Max Sett."].map(h => (
                            <th key={h} style={{position:"sticky",top:0,
                              background:"#E8EDF5",padding:"5px 10px",
                              textAlign: h === "Stagione" ? "left" : "right",
                              fontSize:9,fontWeight:700,color:"#1B2846",
                              fontFamily:"'DM Sans',sans-serif",
                              textTransform:"uppercase",letterSpacing:"0.04em",
                              borderBottom:"1px solid #E8E8E4"}}>
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {analisi.stagioni.map(s => (
                          <tr key={s.stagione}>
                            <td style={{padding:"4px 10px",fontWeight:600,
                              borderBottom:"1px solid #F4F4F0"}}>
                              {s.stagione}
                            </td>
                            <td style={{padding:"4px 10px",textAlign:"right",
                              fontFamily:"'DM Sans',sans-serif",
                              borderBottom:"1px solid #F4F4F0"}}>
                              {s.eventi?.toLocaleString("it-IT")}
                            </td>
                            <td style={{padding:"4px 10px",textAlign:"right",
                              fontFamily:"'DM Sans',sans-serif",color:"#6B6B66",
                              borderBottom:"1px solid #F4F4F0"}}>
                              {s.pct_ev?.toFixed(1)}%
                            </td>
                            <td style={{padding:"4px 10px",textAlign:"right",
                              fontFamily:"'DM Sans',sans-serif",fontWeight:600,
                              color: s.punti >= 0 ? "#15803D" : "#DC2626",
                              borderBottom:"1px solid #F4F4F0"}}>
                              {s.punti >= 0 ? "+" : ""}{s.punti?.toFixed(2)}
                            </td>
                            <td style={{padding:"4px 10px",textAlign:"right",
                              fontFamily:"'DM Sans',sans-serif",
                              color: s.ppe >= 0 ? "#15803D" : "#DC2626",
                              borderBottom:"1px solid #F4F4F0"}}>
                              {s.ppe >= 0 ? "+" : ""}{s.ppe?.toFixed(2)}
                            </td>
                            <td style={{padding:"4px 10px",textAlign:"right",
                              fontFamily:"'DM Sans',sans-serif",color:"#6B6B66",
                              borderBottom:"1px solid #F4F4F0"}}>
                              {s.pct_vincenti != null ? s.pct_vincenti.toFixed(1) + "%" : "—"}
                            </td>
                            <td style={{padding:"4px 10px",textAlign:"right",
                              fontFamily:"'DM Sans',sans-serif",color:"#DC2626",
                              borderBottom:"1px solid #F4F4F0"}}>
                              {s.max_drawdown != null ? s.max_drawdown.toFixed(2) : "—"}
                            </td>
                            <td style={{padding:"4px 10px",textAlign:"right",
                              fontFamily:"'DM Sans',sans-serif",color:"#6B6B66",
                              borderBottom:"1px solid #F4F4F0"}}>
                              {_fmtQuotaMedia(s.quota_media_punta, s.quota_media_banca)}
                            </td>
                            <td style={{padding:"4px 10px",textAlign:"right",
                              fontFamily:"'DM Sans',sans-serif",color:"#7C3AED",
                              borderBottom:"1px solid #F4F4F0"}}>
                              {s.esp_media != null ? s.esp_media.toFixed(2) : "—"}
                            </td>
                            <td style={{padding:"4px 10px",textAlign:"right",
                              fontFamily:"'DM Sans',sans-serif",color:"#7C3AED",
                              borderBottom:"1px solid #F4F4F0"}}>
                              {s.espo_max_giornaliera != null ? s.espo_max_giornaliera.toFixed(2) : "—"}
                            </td>
                            <td style={{padding:"4px 10px",textAlign:"right",
                              fontFamily:"'DM Sans',sans-serif",color:"#7C3AED",
                              borderBottom:"1px solid #F4F4F0"}}>
                              {s.espo_max_settimanale != null ? s.espo_max_settimanale.toFixed(2) : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* ===== GRAFICO EQUITY LINE ===== */}
              {analisi.equity_line && analisi.equity_line.length > 0 && (
                <div style={{background:"#fff",border:"1px solid #E8E8E4",
                  borderRadius:10,overflow:"hidden",marginBottom:16}}>
                  <div style={{padding:"6px 14px",background:"#1B2846"}}>
                    <span style={{fontSize:10,fontWeight:700,color:"#ffffff",
                      textTransform:"uppercase",letterSpacing:"0.06em"}}>
                      Equity Line
                    </span>

                  </div>
                  <div style={{padding:"12px 8px 8px"}}>
                    <ResponsiveContainer width="100%" height={320}>
                      <LineChart data={analisi.equity_line}
                        margin={{top:24,right:20,bottom:24,left:10}}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#F0F0EC" />
                        <XAxis dataKey="data"
                          ticks={ticksStagioni}
                          tickFormatter={(v) => mappaDataStagione[v] || ""}
                          tick={{fontSize:10,fill:"#64748B",fontFamily:"'DM Sans',sans-serif"}}
                          axisLine={{stroke:"#CBD5E1"}} tickLine={true}
                          interval={0} height={22} />
                        <YAxis tick={{fontSize:10,fill:"#94A3B8",
                          fontFamily:"'DM Sans',sans-serif"}}
                          tickFormatter={(v) => v.toFixed(1)} />
                        <Tooltip content={<TooltipEquity />} />
                        <ReferenceLine y={0} stroke="#D8D8D4" strokeDasharray="4 4" />
                        {cambiStagione.map(cs => (
                          <ReferenceLine
                            key={`eq-${cs.idx}`}
                            x={cs.data}
                            stroke="#CBD5E1" strokeDasharray="2 3"
                          />
                        ))}
                        <Line type="monotone" dataKey="punti_prog"
                          stroke="#2563EB" strokeWidth={2}
                          dot={(props) => {
                            const { cx, cy, payload, index } = props;
                            if (!puntoMaxPL || payload.n !== puntoMaxPL.n) return null;
                            const label = `Max +${puntoMaxPL.punti_prog.toFixed(2)}`;
                            const charW = 6.5;
                            const padX = 6;
                            const rectW = label.length * charW + padX * 2;
                            const rectH = 16;
                            return (
                              <g key={`maxpl-${index}`} style={{pointerEvents:"none"}}>
                                <rect x={cx - rectW/2} y={cy - 22 - rectH/2}
                                  width={rectW} height={rectH} rx={4}
                                  fill="#fff" stroke="#15803D" strokeWidth={1} />
                                <text x={cx} y={cy - 22 + 4} textAnchor="middle"
                                  fontSize={11} fontWeight={700} fill="#15803D"
                                  fontFamily="'DM Sans',sans-serif">
                                  {label}
                                </text>
                                <circle cx={cx} cy={cy} r={6} fill="#15803D" stroke="#fff" strokeWidth={2} />
                              </g>
                            );
                          }}
                          activeDot={{r:4,fill:"#2563EB"}} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* ===== GRAFICO DRAWDOWN ===== */}
              {analisi.equity_line && analisi.equity_line.length > 0 && (
                <div style={{background:"#fff",border:"1px solid #E8E8E4",
                  borderRadius:10,overflow:"hidden",marginBottom:16}}>
                  <div style={{padding:"6px 14px",background:"#1B2846"}}>
                    <span style={{fontSize:10,fontWeight:700,color:"#ffffff",
                      textTransform:"uppercase",letterSpacing:"0.06em"}}>
                      Drawdown
                    </span>
                  </div>
                  <div style={{padding:"12px 8px 8px"}}>
                    <ResponsiveContainer width="100%" height={200}>
                      <AreaChart data={analisi.equity_line}
                        margin={{top:5,right:20,bottom:24,left:10}}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#F0F0EC" />
                        <XAxis dataKey="data"
                          ticks={ticksStagioni}
                          tickFormatter={(v) => mappaDataStagione[v] || ""}
                          tick={{fontSize:10,fill:"#64748B",fontFamily:"'DM Sans',sans-serif"}}
                          axisLine={{stroke:"#CBD5E1"}} tickLine={true}
                          interval={0} height={22} />
                        <YAxis tick={{fontSize:10,fill:"#94A3B8",
                          fontFamily:"'DM Sans',sans-serif"}}
                          tickFormatter={(v) => v === 0 ? "0" : `-${v.toFixed(1)}`}
                          reversed={true}
                          domain={[0, 'dataMax']} />
                        <Tooltip content={<TooltipDrawdown />} />
                        {cambiStagione.map(cs => (
                          <ReferenceLine
                            key={`dd-${cs.idx}`}
                            x={cs.data}
                            stroke="#CBD5E1" strokeDasharray="2 3"
                          />
                        ))}
                        <Area type="monotone" dataKey="drawdown"
                          stroke="#DC2626" strokeWidth={1.5}
                          fill="#FECACA" fillOpacity={0.6} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* ===== BREAKDOWN PER LEGA ===== */}
              {analisi.per_lega && Object.keys(analisi.per_lega).length > 0 && (
                <div style={{background:"#fff",border:"1px solid #E8E8E4",
                  borderRadius:10,overflow:"hidden",marginBottom:16}}>
                  <div style={{padding:"6px 14px",background:"#1B2846"}}>
                    <span style={{fontSize:10,fontWeight:700,color:"#ffffff",
                      textTransform:"uppercase",letterSpacing:"0.06em"}}>
                      Breakdown per lega
                    </span>
                  </div>
                  <div style={{maxHeight:280,overflowY:"auto"}}>
                    <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
                      <thead>
                        <tr>
                          {["Lega","Eventi","% Ev.","Punti","PPE","Win Rate","Max DD","Quota Media"].map(h => (
                            <th key={h} style={{position:"sticky",top:0,
                              background:"#E8EDF5",padding:"5px 10px",
                              textAlign: h === "Lega" ? "left" : "right",
                              fontSize:9,fontWeight:700,color:"#1B2846",
                              fontFamily:"'DM Sans',sans-serif",
                              textTransform:"uppercase",letterSpacing:"0.04em",
                              borderBottom:"1px solid #E8E8E4"}}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(analisi.per_lega).map(([lega, v]) => (
                          <tr key={lega}>
                            <td style={{padding:"4px 10px",fontWeight:600,
                              borderBottom:"1px solid #F4F4F0",fontSize:10}}>{lega}</td>
                            <td style={{padding:"4px 10px",textAlign:"right",
                              fontFamily:"'DM Sans',sans-serif",
                              borderBottom:"1px solid #F4F4F0"}}>{v.eventi}</td>
                            <td style={{padding:"4px 10px",textAlign:"right",
                              fontFamily:"'DM Sans',sans-serif",color:"#6B6B66",
                              borderBottom:"1px solid #F4F4F0"}}>{v.pct_ev?.toFixed(1)}%</td>
                            <td style={{padding:"4px 10px",textAlign:"right",
                              fontFamily:"'DM Sans',sans-serif",fontWeight:600,
                              color: v.punti >= 0 ? "#15803D" : "#DC2626",
                              borderBottom:"1px solid #F4F4F0"}}>
                              {v.punti >= 0 ? "+" : ""}{v.punti?.toFixed(2)}</td>
                            <td style={{padding:"4px 10px",textAlign:"right",
                              fontFamily:"'DM Sans',sans-serif",
                              color: v.ppe >= 0 ? "#15803D" : "#DC2626",
                              borderBottom:"1px solid #F4F4F0"}}>
                              {v.ppe >= 0 ? "+" : ""}{v.ppe?.toFixed(2)}</td>
                            <td style={{padding:"4px 10px",textAlign:"right",
                              fontFamily:"'DM Sans',sans-serif",color:"#6B6B66",
                              borderBottom:"1px solid #F4F4F0"}}>
                              {v.pct_vincenti != null ? v.pct_vincenti.toFixed(1) + "%" : "—"}</td>
                            <td style={{padding:"4px 10px",textAlign:"right",
                              fontFamily:"'DM Sans',sans-serif",color:"#DC2626",
                              borderBottom:"1px solid #F4F4F0"}}>
                              {v.max_drawdown != null ? v.max_drawdown.toFixed(2) : "—"}</td>
                            <td style={{padding:"4px 10px",textAlign:"right",
                              fontFamily:"'DM Sans',sans-serif",color:"#6B6B66",
                              borderBottom:"1px solid #F4F4F0"}}>
                              {_fmtQuotaMedia(v.quota_media_punta, v.quota_media_banca)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* ===== MATRICE PUNTI STAGIONE/LEGA ===== */}
              {analisi.punti_stagione_lega && Object.keys(analisi.punti_stagione_lega).length > 0 && (() => {
                const stagioni = Object.keys(analisi.punti_stagione_lega).sort();
                const legheSet = new Set();
                stagioni.forEach(s => Object.keys(analisi.punti_stagione_lega[s]).forEach(l => legheSet.add(l)));
                const leghe = [...legheSet].sort();
                const totStag = {};
                stagioni.forEach(s => { totStag[s] = Object.values(analisi.punti_stagione_lega[s]).reduce((a,b) => a+b, 0); });
                const totLega = {};
                leghe.forEach(l => { totLega[l] = stagioni.reduce((a,s) => a + (analisi.punti_stagione_lega[s]?.[l] || 0), 0); });
                const totGlob = Object.values(totStag).reduce((a,b) => a+b, 0);
                const fmtP = v => v != null && v !== 0 ? v.toFixed(2) : "—";
                const colP = v => v > 0 ? "#15803D" : v < 0 ? "#DC2626" : "#94A3B8";
                return (
                  <div style={{background:"#fff",border:"1px solid #E8E8E4",borderRadius:10,overflow:"hidden",marginBottom:16}}>
                    <div style={{padding:"6px 14px",background:"#1B2846"}}>
                      <span style={{fontSize:10,fontWeight:700,color:"#ffffff",textTransform:"uppercase",letterSpacing:"0.06em"}}>Punti Stagione / Lega</span>
                    </div>
                    <div style={{overflowX:"auto"}}>
                      <table style={{width:"max-content",borderCollapse:"collapse",fontSize:10}}>
                        <thead><tr>
                          <th style={{position:"sticky",left:0,background:"#E8EDF5",padding:"5px 8px",textAlign:"left",fontSize:9,fontWeight:700,color:"#1B2846",borderBottom:"1px solid #E8E8E4",zIndex:2}}>Stag.\Lega</th>
                          {leghe.map(l => (<th key={l} style={{background:"#E8EDF5",padding:"5px 6px",textAlign:"right",fontSize:8,fontWeight:700,color:"#1B2846",borderBottom:"1px solid #E8E8E4",whiteSpace:"nowrap"}}>{l}</th>))}
                          <th style={{position:"sticky",right:0,background:"#E8EDF5",padding:"5px 10px",textAlign:"right",fontSize:9,fontWeight:700,color:"#1B2846",borderBottom:"1px solid #E8E8E4",borderLeft:"1px solid #C8D0E0",zIndex:2,boxShadow:"-4px 0 8px -4px rgba(0,0,0,0.08)"}}>TOT</th>
                        </tr></thead>
                        <tbody>
                          {stagioni.map(stag => (
                            <tr key={stag}>
                              <td style={{position:"sticky",left:0,background:"#fff",padding:"4px 8px",fontWeight:600,borderBottom:"1px solid #F4F4F0",zIndex:2}}>{stag}</td>
                              {leghe.map(lega => { const val = analisi.punti_stagione_lega[stag]?.[lega]; return (
                                <td key={lega} style={{padding:"4px 6px",textAlign:"right",fontFamily:"'DM Sans',sans-serif",fontSize:10,color:colP(val),borderBottom:"1px solid #F4F4F0"}}>{val != null ? fmtP(val) : "—"}</td>
                              );})}
                              <td style={{position:"sticky",right:0,padding:"4px 10px",textAlign:"right",fontFamily:"'DM Sans',sans-serif",fontSize:10,fontWeight:700,color:colP(totStag[stag]),borderBottom:"1px solid #F4F4F0",borderLeft:"1px solid #C8D0E0",background:"#FAFAF7",zIndex:2,boxShadow:"-4px 0 8px -4px rgba(0,0,0,0.08)"}}>{fmtP(totStag[stag])}</td>
                            </tr>
                          ))}
                          <tr style={{background:"#F8F8F5"}}>
                            <td style={{position:"sticky",left:0,background:"#F8F8F5",padding:"4px 8px",fontWeight:700,borderBottom:"1px solid #E8E8E4",zIndex:2}}>Totale</td>
                            {leghe.map(l => (<td key={l} style={{padding:"4px 6px",textAlign:"right",fontFamily:"'DM Sans',sans-serif",fontSize:10,fontWeight:700,color:colP(totLega[l]),borderBottom:"1px solid #E8E8E4"}}>{fmtP(totLega[l])}</td>))}
                            <td style={{position:"sticky",right:0,padding:"4px 10px",textAlign:"right",fontFamily:"'DM Sans',sans-serif",fontSize:10,fontWeight:700,color:colP(totGlob),borderBottom:"1px solid #E8E8E4",borderLeft:"1px solid #C8D0E0",background:"#F8F8F5",zIndex:2,boxShadow:"-4px 0 8px -4px rgba(0,0,0,0.08)"}}>{fmtP(totGlob)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}

              {/* ===== MATRICE EVENTI STAGIONE/LEGA ===== */}
              {analisi.eventi_stagione_lega && Object.keys(analisi.eventi_stagione_lega).length > 0 && (() => {
                const stagioni = Object.keys(analisi.eventi_stagione_lega).sort();
                const legheSet = new Set();
                stagioni.forEach(s => Object.keys(analisi.eventi_stagione_lega[s]).forEach(l => legheSet.add(l)));
                const leghe = [...legheSet].sort();
                const totStag = {};
                stagioni.forEach(s => { totStag[s] = Object.values(analisi.eventi_stagione_lega[s]).reduce((a,b) => a+b, 0); });
                const totLega = {};
                leghe.forEach(l => { totLega[l] = stagioni.reduce((a,s) => a + (analisi.eventi_stagione_lega[s]?.[l] || 0), 0); });
                const totGlob = Object.values(totStag).reduce((a,b) => a+b, 0);
                return (
                  <div style={{background:"#fff",border:"1px solid #E8E8E4",borderRadius:10,overflow:"hidden",marginBottom:16}}>
                    <div style={{padding:"6px 14px",background:"#1B2846"}}>
                      <span style={{fontSize:10,fontWeight:700,color:"#ffffff",textTransform:"uppercase",letterSpacing:"0.06em"}}>Eventi Stagione / Lega</span>
                    </div>
                    <div style={{overflowX:"auto"}}>
                      <table style={{width:"max-content",borderCollapse:"collapse",fontSize:10}}>
                        <thead><tr>
                          <th style={{position:"sticky",left:0,background:"#E8EDF5",padding:"5px 8px",textAlign:"left",fontSize:9,fontWeight:700,color:"#1B2846",borderBottom:"1px solid #E8E8E4",zIndex:2}}>Stag.\Lega</th>
                          {leghe.map(l => (<th key={l} style={{background:"#E8EDF5",padding:"5px 6px",textAlign:"right",fontSize:8,fontWeight:700,color:"#1B2846",borderBottom:"1px solid #E8E8E4",whiteSpace:"nowrap"}}>{l}</th>))}
                          <th style={{position:"sticky",right:0,background:"#E8EDF5",padding:"5px 10px",textAlign:"right",fontSize:9,fontWeight:700,color:"#1B2846",borderBottom:"1px solid #E8E8E4",borderLeft:"1px solid #C8D0E0",zIndex:2,boxShadow:"-4px 0 8px -4px rgba(0,0,0,0.08)"}}>TOT</th>
                        </tr></thead>
                        <tbody>
                          {stagioni.map(stag => (
                            <tr key={stag}>
                              <td style={{position:"sticky",left:0,background:"#fff",padding:"4px 8px",fontWeight:600,borderBottom:"1px solid #F4F4F0",zIndex:2}}>{stag}</td>
                              {leghe.map(lega => { const val = analisi.eventi_stagione_lega[stag]?.[lega]; return (
                                <td key={lega} style={{padding:"4px 6px",textAlign:"right",fontFamily:"'DM Sans',sans-serif",fontSize:10,color: val > 0 ? "#1A1A18" : "#94A3B8",borderBottom:"1px solid #F4F4F0"}}>{val != null ? val : "—"}</td>
                              );})}
                              <td style={{position:"sticky",right:0,padding:"4px 10px",textAlign:"right",fontFamily:"'DM Sans',sans-serif",fontSize:10,fontWeight:700,color:"#1D4ED8",borderBottom:"1px solid #F4F4F0",borderLeft:"1px solid #C8D0E0",background:"#FAFAF7",zIndex:2,boxShadow:"-4px 0 8px -4px rgba(0,0,0,0.08)"}}>{totStag[stag]}</td>
                            </tr>
                          ))}
                          <tr style={{background:"#F8F8F5"}}>
                            <td style={{position:"sticky",left:0,background:"#F8F8F5",padding:"4px 8px",fontWeight:700,borderBottom:"1px solid #E8E8E4",zIndex:2}}>Totale</td>
                            {leghe.map(l => (<td key={l} style={{padding:"4px 6px",textAlign:"right",fontFamily:"'DM Sans',sans-serif",fontSize:10,fontWeight:700,color:"#1A1A18",borderBottom:"1px solid #E8E8E4"}}>{totLega[l]}</td>))}
                            <td style={{position:"sticky",right:0,padding:"4px 10px",textAlign:"right",fontFamily:"'DM Sans',sans-serif",fontSize:10,fontWeight:700,color:"#1D4ED8",borderBottom:"1px solid #E8E8E4",borderLeft:"1px solid #C8D0E0",background:"#F8F8F5",zIndex:2,boxShadow:"-4px 0 8px -4px rgba(0,0,0,0.08)"}}>{totGlob}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}

              {/* ===== MATRICE PPE STAGIONE/LEGA ===== */}
              {analisi.punti_stagione_lega && analisi.eventi_stagione_lega && Object.keys(analisi.punti_stagione_lega).length > 0 && (() => {
                const stagioni = Object.keys(analisi.punti_stagione_lega).sort();
                const legheSet = new Set();
                stagioni.forEach(s => Object.keys(analisi.punti_stagione_lega[s]).forEach(l => legheSet.add(l)));
                const leghe = [...legheSet].sort();
                const fmtPpe = v => v != null ? v.toFixed(2) : "—";
                const colPpe = v => v == null ? "#94A3B8" : v > 0 ? "#15803D" : v < 0 ? "#DC2626" : "#94A3B8";
                // Media ponderata per ogni lega su tutte le stagioni: somma punti / somma eventi
                const totPuntiLega = {};
                const totEventiLega = {};
                leghe.forEach(l => {
                  totPuntiLega[l] = stagioni.reduce((a,s) => a + (analisi.punti_stagione_lega[s]?.[l] || 0), 0);
                  totEventiLega[l] = stagioni.reduce((a,s) => a + (analisi.eventi_stagione_lega[s]?.[l] || 0), 0);
                });
                const mediaPondLega = l => totEventiLega[l] > 0 ? totPuntiLega[l] / totEventiLega[l] : null;
                const tooltipMedia = "Media ponderata = somma punti / somma eventi della lega su tutte le stagioni. Coincide col PPE complessivo del sistema quando c'e' una sola lega.";
                return (
                  <div style={{background:"#fff",border:"1px solid #E8E8E4",borderRadius:10,overflow:"hidden",marginBottom:16}}>
                    <div style={{padding:"6px 14px",background:"#1B2846"}}>
                      <span style={{fontSize:10,fontWeight:700,color:"#ffffff",textTransform:"uppercase",letterSpacing:"0.06em"}}>PPE Stagione / Lega</span>
                    </div>
                    <div style={{overflowX:"auto"}}>
                      <table style={{width:"max-content",borderCollapse:"collapse",fontSize:10}}>
                        <thead><tr>
                          <th style={{position:"sticky",left:0,background:"#E8EDF5",padding:"5px 8px",textAlign:"left",fontSize:9,fontWeight:700,color:"#1B2846",borderBottom:"1px solid #E8E8E4",zIndex:2}}>Stag.\Lega</th>
                          {leghe.map(l => (<th key={l} style={{background:"#E8EDF5",padding:"5px 6px",textAlign:"right",fontSize:8,fontWeight:700,color:"#1B2846",borderBottom:"1px solid #E8E8E4",whiteSpace:"nowrap"}}>{l}</th>))}
                        </tr></thead>
                        <tbody>
                          {stagioni.map(stag => (
                            <tr key={stag}>
                              <td style={{position:"sticky",left:0,background:"#fff",padding:"4px 8px",fontWeight:600,borderBottom:"1px solid #F4F4F0",zIndex:2}}>{stag}</td>
                              {leghe.map(lega => {
                                const pt = analisi.punti_stagione_lega[stag]?.[lega];
                                const ev = analisi.eventi_stagione_lega[stag]?.[lega];
                                const ppeCella = (ev != null && ev > 0) ? pt / ev : null;
                                return (
                                  <td key={lega} style={{padding:"4px 6px",textAlign:"right",fontFamily:"'DM Sans',sans-serif",fontSize:10,color:colPpe(ppeCella),borderBottom:"1px solid #F4F4F0"}}>{fmtPpe(ppeCella)}</td>
                                );
                              })}
                            </tr>
                          ))}
                          <tr style={{background:"#F8F8F5"}} title={tooltipMedia}>
                            <td style={{position:"sticky",left:0,background:"#F8F8F5",padding:"4px 8px",fontWeight:700,borderBottom:"1px solid #E8E8E4",zIndex:2,cursor:"help"}}>Media</td>
                            {leghe.map(l => {
                              const mp = mediaPondLega(l);
                              return (
                                <td key={l} style={{padding:"4px 6px",textAlign:"right",fontFamily:"'DM Sans',sans-serif",fontSize:10,fontWeight:700,color:colPpe(mp),borderBottom:"1px solid #E8E8E4",cursor:"help"}}>{fmtPpe(mp)}</td>
                              );
                            })}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}

              {/* ===== PUNTI PER MESE ===== */}
              {analisi.punti_mese && Object.keys(analisi.punti_mese).length > 0 && (() => {
                const mesi = ["Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre",
                  "Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","TOT"];
                const righe = Object.keys(analisi.punti_mese)
                  .filter(k => !k.startsWith("_")).sort();
                const totale = analisi.punti_mese["_Totale"];
                const media = analisi.punti_mese["_Media"];
                return (
                  <div style={{background:"#fff",border:"1px solid #E8E8E4",
                    borderRadius:10,overflow:"hidden",marginBottom:16}}>
                    <div style={{padding:"6px 14px",background:"#1B2846"}}>
                      <span style={{fontSize:10,fontWeight:700,color:"#ffffff",
                        textTransform:"uppercase",letterSpacing:"0.06em"}}>
                        Punti per mese
                      </span>
                    </div>
                    <div style={{overflowX:"auto"}}>
                      <table style={{width:"100%",borderCollapse:"collapse",fontSize:10}}>
                        <thead>
                          <tr>
                            <th style={{position:"sticky",left:0,background:"#E8EDF5",padding:"5px 8px",
                              textAlign:"left",fontSize:9,fontWeight:700,color:"#1B2846",
                              fontFamily:"'DM Sans',sans-serif",
                              borderBottom:"1px solid #E8E8E4",zIndex:1}}></th>
                            {mesi.map(m => (
                              <th key={m} style={{background:"#E8EDF5",padding:"5px 6px",
                                textAlign:"right",fontSize:8,fontWeight:700,
                                fontFamily:"'DM Sans',sans-serif",
                                color: m === "TOT" ? "#1B2846" : "#1B2846",
                                borderBottom:"1px solid #E8E8E4",whiteSpace:"nowrap"}}>{m}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {righe.map(stag => (
                            <tr key={stag}>
                              <td style={{position:"sticky",left:0,background:"#fff",
                                padding:"4px 8px",fontWeight:600,borderBottom:"1px solid #F4F4F0",
                                zIndex:1}}>{stag}</td>
                              {mesi.map(m => {
                                const val = analisi.punti_mese[stag]?.[m];
                                return (
                                  <td key={m} style={{padding:"4px 6px",textAlign:"right",
                                    fontFamily:"'DM Sans',sans-serif",fontSize:10,
                                    fontWeight: m === "TOT" ? 700 : 400,
                                    color: val > 0 ? "#15803D" : val < 0 ? "#DC2626" : "#94A3B8",
                                    borderBottom:"1px solid #F4F4F0"}}>
                                    {val != null && val !== 0 ? val.toFixed(2) : "—"}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                          {totale && (
                            <tr style={{background:"#F8F8F5"}}>
                              <td style={{position:"sticky",left:0,background:"#F8F8F5",
                                padding:"4px 8px",fontWeight:700,borderBottom:"1px solid #E8E8E4",
                                zIndex:1}}>Totale</td>
                              {mesi.map(m => {
                                const val = totale[m];
                                return (
                                  <td key={m} style={{padding:"4px 6px",textAlign:"right",
                                    fontFamily:"'DM Sans',sans-serif",fontSize:10,fontWeight:700,
                                    color: val > 0 ? "#15803D" : val < 0 ? "#DC2626" : "#94A3B8",
                                    borderBottom:"1px solid #E8E8E4"}}>
                                    {val != null && val !== 0 ? val.toFixed(2) : "—"}
                                  </td>
                                );
                              })}
                            </tr>
                          )}
                          {media && (
                            <tr style={{background:"#F8F8F5"}}>
                              <td style={{position:"sticky",left:0,background:"#F8F8F5",
                                padding:"4px 8px",fontWeight:700,fontStyle:"italic",
                                borderBottom:"1px solid #E8E8E4",zIndex:1}}>Media</td>
                              {mesi.map(m => {
                                const val = media[m];
                                return (
                                  <td key={m} style={{padding:"4px 6px",textAlign:"right",
                                    fontFamily:"'DM Sans',sans-serif",fontSize:10,
                                    fontStyle:"italic",
                                    color: val > 0 ? "#15803D" : val < 0 ? "#DC2626" : "#94A3B8",
                                    borderBottom:"1px solid #E8E8E4"}}>
                                    {val != null && val !== 0 ? val.toFixed(2) : "—"}
                                  </td>
                                );
                              })}
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}

              {/* ===== TASTO ESPORTA EXCEL ===== */}
              <div style={{marginTop:16,display:"flex",justifyContent:"flex-end",gap:8}}>
                <button onClick={apriDrawer}
                  style={{...btnGhost,fontSize:12,padding:"8px 16px"}}>
                  👁 Visualizza eventi
                </button>
                <button onClick={esportaExcel}
                  style={{...btnSave,fontSize:12,padding:"8px 16px"}}>
                  📥 Estrai eventi
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ===== DRAWER VISUALIZZA EVENTI ===== */}
      {drawerAperto && (
        <>
          <div onClick={() => setDrawerAperto(false)}
            style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.35)",zIndex:1000}} />
          <div style={{
            position:"fixed",top:0,right:0,bottom:0,
            width:"calc(100vw - 220px)",
            background:"#fff",boxShadow:"-4px 0 24px rgba(0,0,0,0.15)",
            zIndex:1001,display:"flex",flexDirection:"column",
          }}>
            {/* Header drawer */}
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
              padding:"16px 24px",borderBottom:"1px solid #E2E8F0",background:"#1B2846"}}>
              <div>
                <span style={{fontSize:15,fontWeight:700,color:"#fff"}}>
                  👁 Visualizza eventi — {nomeSistema}
                </span>
                {!drawerLoading && (
                  <span style={{marginLeft:12,fontSize:12,color:"#94A3B8"}}>
                    {drawerPartite.length} eventi
                  </span>
                )}
              </div>
              <button onClick={() => setDrawerAperto(false)}
                style={{background:"none",border:"none",color:"#94A3B8",fontSize:20,cursor:"pointer",lineHeight:1}}>
                ✕
              </button>
            </div>
            {/* Body drawer */}
            <div style={{flex:1,overflowY:"auto",padding:"0"}}>
              {drawerLoading ? (
                <div style={{padding:32,textAlign:"center",color:"#64748B"}}>Caricamento eventi…</div>
              ) : drawerPartite.length === 0 ? (
                <div style={{padding:32,textAlign:"center",color:"#64748B"}}>Nessun evento trovato.</div>
              ) : (
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                  <thead>
                    <tr style={{background:"#E8EDF5",position:"sticky",top:0}}>
                      {["Stagione","Lega","Data","Ora","Casa","Trasferta","Mercato","Quota","Punti","Cumulato P/L","Max P/L","Drawdown"].map(h => (
                        <th key={h} style={{padding:"8px 12px",textAlign:"left",fontWeight:600,
                          color:"#1B2846",borderBottom:"2px solid #CBD5E1",whiteSpace:"nowrap"}}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {drawerPartite.map((p, i) => (
                      <tr key={i} style={{background: i%2===0 ? "#fff" : "#F8FAFC"}}>
                        <td style={{padding:"7px 12px",color:"#475569",whiteSpace:"nowrap"}}>{p.stagione}</td>
                        <td style={{padding:"7px 12px",color:"#475569"}}>{p.lega}</td>
                        <td style={{padding:"7px 12px",color:"#475569",whiteSpace:"nowrap"}}>{p.data ? (() => { const d = new Date(p.data); return d.toLocaleDateString('it-IT',{weekday:'short',day:'2-digit',month:'2-digit',year:'numeric'}); })() : "—"}</td>
                        <td style={{padding:"7px 12px",color:"#475569",whiteSpace:"nowrap"}}>{p.time_it ? p.time_it.slice(0,5) : "—"}</td>
                        <td style={{padding:"7px 12px",color:"#1E293B",fontWeight:500}}>{p.casa}</td>
                        <td style={{padding:"7px 12px",color:"#1E293B",fontWeight:500}}>{p.trasferta}</td>
                        <td style={{padding:"7px 12px",color:"#475569",whiteSpace:"nowrap"}}>{p.mercato}</td>
                        <td style={{padding:"7px 12px",color:"#475569",textAlign:"right",whiteSpace:"nowrap"}}>{(() => {
                          const qp = p.quota_punta;
                          const qb = p.quota_banca;
                          const hasP = qp !== null && qp !== undefined;
                          const hasB = qb !== null && qb !== undefined;
                          if (hasP && hasB) return `${qp} / ${qb}`;
                          if (hasP) return qp;
                          if (hasB) return qb;
                          return p.quota ?? "—";
                        })()}</td>
                        <td style={{padding:"7px 12px",textAlign:"right",fontWeight:600,
                          color: p.punti > 0 ? "#15803D" : p.punti < 0 ? "#DC2626" : "#475569"}}>
                          {p.punti}
                        </td>
                        <td style={{padding:"7px 12px",textAlign:"right",fontWeight:600,whiteSpace:"nowrap",
                          color: p.cumulato_pl > 0 ? "#15803D" : p.cumulato_pl < 0 ? "#DC2626" : "#475569"}}>
                          {p.cumulato_pl != null ? p.cumulato_pl.toFixed(2) : "—"}
                        </td>
                        <td style={{padding:"7px 12px",textAlign:"right",color:"#475569",whiteSpace:"nowrap"}}>
                          {p.massimo_storico != null ? p.massimo_storico.toFixed(2) : "—"}
                        </td>
                        <td style={{padding:"7px 12px",textAlign:"right",fontWeight:600,whiteSpace:"nowrap",
                          background: p.drawdown > 0 ? "#FEE2E2" : "transparent",
                          color: p.drawdown > 0 ? "#991B1B" : "#94A3B8"}}>
                          {p.drawdown != null && p.drawdown > 0 ? `-${p.drawdown.toFixed(2)}` : "0"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
